import React, { useState, useEffect } from 'react';
import { useSite } from '../../context/SiteContext';
import { dbService } from '../../services/firebase';
import { YachtMarina } from '../../types';
import { EmptySiteNotice } from '../common/EmptySiteNotice';
import {
  Anchor,
  Plus,
  Edit,
  Trash2,
  MapPin,
  Search,
  Sparkles,
  Ship,
  ShieldCheck,
} from 'lucide-react';
import { MarinaFormModal } from './MarinaFormModal';

export const MarinaList: React.FC<{ onNavigate?: (view: string) => void }> = ({ onNavigate }) => {
  const { activeSite } = useSite();
  const [marinas, setMarinas] = useState<YachtMarina[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingMarina, setEditingMarina] = useState<YachtMarina | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const loadMarinas = async () => {
    if (!activeSite) return;
    setLoading(true);
    const data = await dbService.getMarinas(activeSite.id);
    setMarinas(data);
    setLoading(false);
  };

  useEffect(() => {
    loadMarinas();
  }, [activeSite?.id]);

  if (!activeSite) {
    return (
      <EmptySiteNotice
        title="Marina & Liman Yönetimi İçin Aktif Web Sitesi Gereklidir"
        description="Fethiye, Göcek, Bodrum marinaları ve liman detaylarını yönetmek için lütfen bir web sitesi oluşturun veya mevcut sitelerinizden birini aktif yapın."
        onOpenCreateSite={() => onNavigate && onNavigate('sites')}
        onReturnToDashboard={() => onNavigate && onNavigate('dashboard')}
      />
    );
  }

  const handleDelete = async (marinaId: string) => {
    if (confirm('Bu marinayı listeden silmek istediğinizden emin misiniz?')) {
      await dbService.deleteMarina(activeSite.id, marinaId);
      loadMarinas();
    }
  };

  const filteredMarinas = marinas.filter((m) => {
    const query = (searchQuery || '').toLowerCase();
    return (
      (m.name ? m.name.toLowerCase().includes(query) : false) ||
      (m.location ? m.location.toLowerCase().includes(query) : false) ||
      (m.services && m.services.some((s) => (s ? s.toLowerCase().includes(query) : false)))
    );
  });

  return (
    <div className="p-6 sm:p-8 max-w-7xl mx-auto space-y-6 animate-in fade-in select-none">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-cyan-600">
            <Anchor className="w-5 h-5" />
            <span className="text-xs font-bold uppercase tracking-wider">Bağlama & Transfer Noktaları</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
            Marinalar & Limanlar Yönetimi
          </h1>
          <p className="text-xs sm:text-sm text-slate-500">
            Teknelerinizin bağlı olduğu marinaları, teknik kapasitelerini ve transfer imkanlarını yönetin.
          </p>
        </div>

        <button
          onClick={() => {
            setEditingMarina(null);
            setIsModalOpen(true);
          }}
          className="px-4 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-bold text-xs shadow-lg shadow-cyan-500/20 transition flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Yeni Marina Ekle
        </button>
      </div>

      {/* Search Bar */}
      <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between gap-4">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
          <input
            type="text"
            placeholder="Marina veya şehir ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-800 focus:bg-white focus:border-cyan-500 outline-none transition"
          />
        </div>

        <div className="text-xs text-slate-500 font-medium">
          Toplam <span className="font-bold text-slate-800">{marinas.length}</span> marina listeleniyor
        </div>
      </div>

      {/* Marinas Grid */}
      {loading ? (
        <div className="p-12 text-center text-slate-400">Marinalar yükleniyor...</div>
      ) : filteredMarinas.length === 0 ? (
        <div className="p-12 bg-white rounded-3xl border border-slate-200 text-center space-y-3">
          <Anchor className="w-12 h-12 text-slate-300 mx-auto" />
          <h3 className="text-base font-bold text-slate-800">Henüz Marina Eklenmemiş</h3>
          <p className="text-xs text-slate-500 max-w-sm mx-auto">
            İlk marinanızı ekleyerek charter müşterilerinize bağlama noktalarını ve transfer olanaklarını gösterin.
          </p>
          <button
            onClick={() => {
              setEditingMarina(null);
              setIsModalOpen(true);
            }}
            className="px-4 py-2 rounded-xl bg-cyan-600 text-white text-xs font-bold hover:bg-cyan-500 transition"
          >
            İlk Marinayı Ekle
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMarinas.map((marina) => (
            <div
              key={marina.id}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col hover:shadow-md transition group"
            >
              {/* Cover Image */}
              <div className="h-48 w-full relative bg-slate-100 overflow-hidden">
                <img
                  src={marina.coverImage}
                  alt={marina.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute top-3 left-3 flex flex-col gap-1.5">
                  <span className="px-2.5 py-1 rounded-lg bg-slate-900/80 backdrop-blur-sm text-white text-[11px] font-bold flex items-center gap-1 shadow-sm">
                    <Ship className="w-3 h-3 text-cyan-400" />
                    {marina.berthCapacity} Tekne
                  </span>
                  {marina.isStarter && (
                    <span className="px-2 py-0.5 rounded-md bg-amber-500/90 text-white text-[9px] font-bold tracking-wider uppercase">
                      Başlangıç Şablonu
                    </span>
                  )}
                </div>
                {marina.isFeatured && (
                  <div className="absolute top-3 right-3 px-2.5 py-1 rounded-lg bg-cyan-600/90 backdrop-blur-sm text-white text-[10px] font-bold flex items-center gap-1 shadow-sm">
                    <Sparkles className="w-3 h-3" />
                    Öne Çıkan
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
                <div>
                  <h3 className="text-base font-bold text-slate-900 line-clamp-1 group-hover:text-cyan-600 transition">
                    {marina.name}
                  </h3>
                  <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                    <MapPin className="w-3.5 h-3.5 text-cyan-500 shrink-0" />
                    <span className="truncate">{marina.location}</span>
                    {marina.coordinates && (
                      <span className="text-[10px] font-mono text-slate-400">
                        ({marina.coordinates})
                      </span>
                    )}
                  </div>
                  {marina.description && (
                    <p className="text-xs text-slate-600 mt-2 line-clamp-2 leading-relaxed">
                      {marina.description}
                    </p>
                  )}
                </div>

                {/* Specs */}
                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100 text-xs">
                  <div className="bg-slate-50 p-2 rounded-xl">
                    <span className="text-[10px] text-slate-400 block font-semibold">Max Su Çekimi:</span>
                    <span className="font-bold text-slate-700">{marina.maxDraft || '6m'}</span>
                  </div>
                  <div className="bg-slate-50 p-2 rounded-xl">
                    <span className="text-[10px] text-slate-400 block font-semibold">Max Boy (LOA):</span>
                    <span className="font-bold text-slate-700">{marina.maxLoa || '70m'}</span>
                  </div>
                </div>

                {/* Services Chips */}
                {marina.services && marina.services.length > 0 && (
                  <div className="pt-2 border-t border-slate-100">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                      Marina Hizmetleri ({marina.services.length})
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {marina.services.slice(0, 3).map((svc, i) => (
                        <span
                          key={i}
                          className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-medium flex items-center gap-1"
                        >
                          <ShieldCheck className="w-2.5 h-2.5 text-cyan-500" />
                          <span>{svc}</span>
                        </span>
                      ))}
                      {marina.services.length > 3 && (
                        <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px] font-semibold">
                          +{marina.services.length - 3}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-[11px] text-slate-400 font-mono">
                    ID: {marina.id.slice(-6)}
                  </span>

                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => {
                        setEditingMarina(marina);
                        setIsModalOpen(true);
                      }}
                      className="p-1.5 rounded-lg text-slate-600 hover:text-cyan-600 hover:bg-cyan-50 transition"
                      title="Marinayı Düzenle"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(marina.id)}
                      className="p-1.5 rounded-lg text-slate-600 hover:text-rose-600 hover:bg-rose-50 transition"
                      title="Marinayı Sil"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Form */}
      <MarinaFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={loadMarinas}
        marinaToEdit={editingMarina}
      />
    </div>
  );
};
